import { useEffect, useMemo } from 'react';
import { Layer, Source, useMap, type MapLayerMouseEvent } from 'react-map-gl';
import type { Polygon } from 'geojson';
import turfBbox from '@turf/bbox';

import type { BBox } from 'components/query-map';

import {
  LISTED_FILL_LAYER,
  NON_TRACT_DOT_LAYER,
  NON_TRACT_FILL_LAYER,
  NON_TRACT_HIT_LAYER,
} from './asset-layers';
import type { BoundingBox, MapQueryTicks, TractSource } from './types';
import {
  FOCUS_COLOR,
  narrowsResults,
  resultFeatureId,
  tractPaint,
  tractTilesUrl,
} from './utils';

export const TRACT_SOURCE_ID = 'package-map-query-tracts';
const SOURCE_LAYER = 'tracts';
export const TRACT_FILL_LAYER = `${TRACT_SOURCE_ID}-fill`;
export const TRACT_LINE_LAYER = `${TRACT_SOURCE_ID}-line`;
/** Invisible, under every Tract drawn: what a click can focus. */
export const TRACT_HIT_LAYER = `${TRACT_SOURCE_ID}-hit`;
export const TRACT_FOCUS_LAYER = `${TRACT_SOURCE_ID}-focus`;
/** A Tract too small for the tile's grid comes as a point on its shape: a dot. */
export const TRACT_DOT_LAYER = `${TRACT_SOURCE_ID}-dot`;
/**
 * Every result drawn, the asset layers' too, in one listener: Mapbox hands it
 * the features under the click topmost first, so a wellbore over a Tract focuses
 * the wellbore, and one click never focuses two results.
 */
const CLICK_LAYERS = [
  TRACT_HIT_LAYER,
  TRACT_DOT_LAYER,
  LISTED_FILL_LAYER,
  NON_TRACT_FILL_LAYER,
  NON_TRACT_HIT_LAYER,
  NON_TRACT_DOT_LAYER,
];

// Mapbox's `==` needs a string to compare with; no Tract id is this.
const NO_FOCUS = '__nofocus__';

export interface TractLayerProps {
  /** The tracts map source: the tile URL and its minzoom. */
  source: TractSource;
  /** A ticked entity's matches include what it holds on this date. */
  asOfDate: string | null;
  ticks: MapQueryTicks;
  /** The drawn polygon. The tiles reload each time it changes, each corner edit included. */
  polygon: Polygon | null;
  /**
   * The visible extent searched, under Use Visible Map Extent: the one the
   * search was sent, once the map has held still. The tiles reload with it.
   */
  extent: BoundingBox | null;
  focusedTractId: string | null;
  /**
   * A click on a result drawn, a Tract here or an asset of the asset layers,
   * with its row id (`resultRowId`) and the box of its part drawn on the map.
   * Omit to ignore clicks, as while drawing a polygon.
   */
  onResultClick?: (rowId: string, bbox: BBox) => void;
}

/**
 * The Map Query map's Tracts: only those the search matches, as the app's tract
 * maps draw only their Tracts. Vector tiles from the tracts map source carry
 * the search's filters, so a change of filters loads new ones; each Tract is
 * filled in its first ticked owner's colour and outlined in the result blue.
 * Narrowed by an entity, a location or a drawn polygon they are drawn at every
 * zoom, as the package pages draw their Tracts; otherwise every Tract (or every
 * one in view) is a match, drawn from the source's minzoom (county scale). The
 * focused Tract gets an orange outline over everything.
 *
 * @param {TractLayerProps} props - The source, date, ticks, the map area, focus and the click handler
 * @return {JSX.Element} The tract source and its layers
 */
export function TractLayer({
  source,
  asOfDate,
  ticks,
  polygon,
  extent,
  focusedTractId,
  onResultClick,
}: TractLayerProps) {
  const { current: map } = useMap();
  const { entityIds, locationGroups } = ticks;
  const tiles = useMemo(
    () => [
      tractTilesUrl(source.tiles, {
        asOfDate,
        entityIds,
        locationGroups,
        polygon,
        extent,
      }),
    ],
    [source.tiles, asOfDate, entityIds, locationGroups, polygon, extent]
  );
  const paint = useMemo(
    () => tractPaint(entityIds, source.minzoom),
    [entityIds, source.minzoom]
  );
  const minzoom = narrowsResults(ticks) ? 0 : source.minzoom;

  useEffect(() => {
    if (!map || !onResultClick) return undefined;
    /**
     * Reports the topmost result clicked.
     *
     * @param {MapLayerMouseEvent} event - The click on the results
     */
    const handleClick = (event: MapLayerMouseEvent) => {
      const feature = event.features?.[0];
      const rowId = feature ? resultFeatureId(feature.properties) : null;
      if (!feature || rowId === null) return;
      onResultClick(rowId, turfBbox(feature) as BBox);
    };
    const canvas = map.getCanvas();
    /** Shows a Tract can be clicked. */
    const pointer = () => {
      canvas.style.cursor = 'pointer';
    };
    /** Puts the map's own cursor back. */
    const plain = () => {
      canvas.style.cursor = '';
    };
    map.on('click', CLICK_LAYERS, handleClick);
    map.on('mousemove', CLICK_LAYERS, pointer);
    map.on('mouseleave', CLICK_LAYERS, plain);
    return () => {
      map.off('click', CLICK_LAYERS, handleClick);
      map.off('mousemove', CLICK_LAYERS, pointer);
      map.off('mouseleave', CLICK_LAYERS, plain);
      plain();
    };
  }, [map, onResultClick]);

  return (
    <Source id={TRACT_SOURCE_ID} type="vector" tiles={tiles}>
      <Layer
        id={TRACT_FILL_LAYER}
        type="fill"
        source-layer={SOURCE_LAYER}
        minzoom={minzoom}
        paint={{
          'fill-color': paint.fillColor,
          'fill-opacity': paint.fillOpacity,
        }}
      />
      <Layer
        id={TRACT_LINE_LAYER}
        type="line"
        source-layer={SOURCE_LAYER}
        minzoom={minzoom}
        paint={{
          'line-color': paint.lineColor,
          'line-width': paint.lineWidth,
        }}
      />
      <Layer
        id={TRACT_HIT_LAYER}
        type="fill"
        source-layer={SOURCE_LAYER}
        minzoom={minzoom}
        paint={{ 'fill-opacity': 0 }}
      />
      <Layer
        id={TRACT_DOT_LAYER}
        type="circle"
        source-layer={SOURCE_LAYER}
        minzoom={minzoom}
        filter={['==', ['geometry-type'], 'Point']}
        paint={{
          'circle-color': paint.fillColor,
          'circle-radius': 3,
          'circle-stroke-width': 1,
          'circle-stroke-color': [
            'case',
            ['==', ['get', 'tract_id'], focusedTractId ?? NO_FOCUS],
            FOCUS_COLOR,
            paint.lineColor,
          ],
        }}
      />
      {/* Always mounted: a layer added later would land above the polygon drawing. */}
      <Layer
        id={TRACT_FOCUS_LAYER}
        type="line"
        source-layer={SOURCE_LAYER}
        minzoom={minzoom}
        filter={['==', ['get', 'tract_id'], focusedTractId ?? NO_FOCUS]}
        paint={{ 'line-color': FOCUS_COLOR, 'line-width': 3 }}
      />
    </Source>
  );
}
